import * as React from 'react';

import type {ErrorReason, MessageStatus} from 'nutshell-graphql-types';

import {colors} from '../../../colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {AlarmClockIcon} from '../../../icon/svg/alarm-clock-icon';
import {WarningIcon} from '../../../icon/svg/warning-icon';
import {RelativeTimestamp} from '../../../timestamp/relative-timestamp';

import type {SecondaryButtonInfo} from './action-buttons';

export type DripSequenceInfo = {
    position: number;
    total: number;
};

export function getSecondaryButtonInfo(
    dripSequenceInfo: DripSequenceInfo | null | undefined,
    handleSecondaryClick: () => any
): SecondaryButtonInfo | null | undefined {
    if (dripSequenceInfo) {
        const numOfMessagesLeftInDripSequenceExcludingCurrentMessage =
            dripSequenceInfo.total - dripSequenceInfo.position;

        let confirmationQuestion =
            'This is the final message of a drip sequence. Are you sure you want to cancel?';
        if (numOfMessagesLeftInDripSequenceExcludingCurrentMessage === 1) {
            confirmationQuestion =
                'Cancelling this message will delete all the messages in this drip sequence. There is 1 more message in queue. Are you sure you want to cancel?';
        } else if (numOfMessagesLeftInDripSequenceExcludingCurrentMessage > 1) {
            confirmationQuestion = `Cancelling this message will delete all the messages in this drip sequence. There are ${numOfMessagesLeftInDripSequenceExcludingCurrentMessage} more messages in queue. Are you sure you want to cancel?`;
        }

        return {
            text: 'Cancel',
            confirmationQuestion: confirmationQuestion,
            confirmationPrimaryText: 'Yes, cancel',
            confirmationSecondaryText: 'No, don’t cancel',
            onClick: handleSecondaryClick,
        };
    }

    return {
        text: 'Delete',
        confirmationQuestion: 'Are you sure you want to delete this message?',
        confirmationPrimaryText: 'Yes, delete',
        confirmationSecondaryText: 'No, don’t delete',
        onClick: handleSecondaryClick,
    };
}

export function shouldShowActionButtons(
    isResending: boolean,
    isDueToSend: boolean,
    isScheduled: boolean,
    isFailed: boolean
): boolean {
    if (isResending) {
        return false;
    } else if (isDueToSend) {
        return false;
    } else if (isScheduled || isFailed) {
        return true;
    }

    return false;
}

export const getIcon = (status: MessageStatus) => {
    switch (status) {
        case 'FAILED':
            // @ts-expect-error
            return <WarningIcon size={12} wrapWithDiv={false} color={colors.error} />;
        case 'SCHEDULED':
            return <AlarmClockIcon size={12} wrapWithDiv={false} color={colors.greyLt} />;
        case 'CANCELLED':
            // @ts-expect-error
            return <WarningIcon size={12} wrapWithDiv={false} color={colors.error} />;
        default:
            return null;
    }
};

export const getStatusText = (status: MessageStatus, scheduledTime?: number) => {
    switch (status) {
        case 'FAILED':
            return 'Failed to send';
        case 'CANCELLED':
            return 'Cancelled';
        case 'SCHEDULED':
            if (scheduledTime) {
                return (
                    <span className='flex'>
                        Scheduled to send&nbsp; <RelativeTimestamp datetime={scheduledTime} />
                    </span>
                );
            }

            return 'Scheduled';
        case 'TRANSIT':
            return 'Sending…';
        default:
            return null;
    }
};

export const getErrorMessage = (errorReason?: ErrorReason | null): string => {
    switch (errorReason) {
        case 'HANDSET_UNAVAILABLE':
            return 'The number is switched off or otherwise unavailable.';
        case 'UNKNOWN_NUMBER':
            return 'The number is unknown and may no longer exist.';
        case 'LANDLINE_OR_UNREACHABLE':
            return 'The number is a landline or unreachable carrier.';
        case 'WHATSAPP_24_HOUR_WINDOW_EXPIRED':
            return 'The 24-hour message window has expired.';
        case 'WHATSAPP_PHONE_REGISTRATION_ERROR':
            return 'The phone number is not registered with WhatsApp.';
        case 'WHATSAPP_PAYMENT_ERROR':
            return 'There was an error related to your payment method.';
        case 'WHATSAPP_RECIPIENT_OPTED_OUT':
            return 'The recipient has opted out of receiving messages from this number.';
        case 'WHATSAPP_MEDIA_UPLOAD_ERROR':
            return 'Unable to upload the media used in the message. Please try again with a different file.';
        case 'UNKNOWN_ERROR':
        default:
            return 'An unknown error occurred';
    }
};
